/** Provides functions for navigating and querying the hierarchical structure of tasks in Firestore. */
// @ts-check
import { generateKeyBetween } from 'fractional-indexing';
import {
  getLastSiblingTask,
  reserveNextOrderKeyInTransaction,
} from '@/services/taskService';

/**
 * Retrieves the next order key for a new task using fractional indexing.
 *
 * When a `transaction` is provided, the key is reserved via the per-parent
 * orderKey anchor inside the transaction, so concurrent appends to the same
 * parent can't generate the same key (see reserveNextOrderKeyInTransaction).
 * Without a transaction, falls back to the old racy read — kept for callers
 * that don't need the guarantee.
 *
 * @param {string} userId
 * @param {string | null} [parentId]
 * @param {import('firebase/firestore').Transaction} [transaction] - when
 *   provided, the key is reserved transactionally via the anchor.
 * @param {string | null} [floorKey] - minimum key for append operations
 * @returns {Promise<string>}
 */
export const getNextOrderKey = async (
  userId,
  parentId = null,
  transaction,
  floorKey = null,
) => {
  try {
    if (transaction) {
      // Plain (non-transactional) read of the current last sibling's
      // orderKey. Seeds the anchor before it exists, and also floors a
      // stale anchor (see reserveNextOrderKeyInTransaction) against
      // orderKeys written outside the anchor path (drag-and-drop, manual
      // edits).
      const lastTask = await getLastSiblingTask(parentId, userId);
      const fallbackLastKey = lastTask?.orderKey || null;
      return await reserveNextOrderKeyInTransaction(
        transaction,
        parentId,
        userId,
        fallbackLastKey,
        floorKey,
      );
    }

    const lastTask = await getLastSiblingTask(parentId, userId);
    if (lastTask) {
      const previousKey = lastTask.orderKey;
      return generateKeyBetween(previousKey, null);
    } else {
      return generateKeyBetween(null, null);
    }
  } catch (error) {
    console.error('Error getting next order key:', error);
    return generateKeyBetween(null, null);
  }
};

/**
 * Transforms a Firestore QuerySnapshot into an array of Task objects.
 * @param {import('firebase/firestore').QuerySnapshot} querySnapshot
 * @returns {Task[]}
 */
/** @public */
export const processTasksSnapshot = (querySnapshot) => {
  const userTasks = [];
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    userTasks.push(
      /** @type {Task} */ ({
        id: doc.id,
        ...data,
      }),
    );
  });
  return userTasks;
};

/**
 * @param {Task[]} allTasks
 */
const getKeysAtEndOfList = (allTasks) => {
  const last = allTasks[allTasks.length - 1];
  return {
    previousKey: last?.orderKey || null,
    nextKey: null,
    parentId: last?.parentId || null,
  };
};

/**
 * @param {Task | undefined} prev
 * @param {Task | undefined} next
 */
const getKeysAtPosition = (prev, next) => ({
  previousKey: prev?.orderKey || null,
  nextKey: next?.orderKey || null,
  parentId: prev?.parentId || next?.parentId || null,
});

/**
 * Retrieves the order keys and parentId of the tasks surrounding a given index.
 * @param {number} index
 * @param {Task[]} allTasks
 */
export const getKeysAtIndex = (index, allTasks) => {
  if (index >= allTasks.length) {
    return getKeysAtEndOfList(allTasks);
  }
  const prev = allTasks[index - 1];
  const next = allTasks[index];
  return getKeysAtPosition(prev, next);
};

/**
 * Generates a hierarchical breadcrumb path for a task.
 * @param {Task} task
 * @param {Map<string, Task>} tasksMap
 * @returns {any[]}
 */
export const generateBreadcrumbPath = (task, tasksMap) => {
  if (!task || !task.parentId) return [];
  const breadcrumbs = [];
  let currentParentId = task.parentId;
  const MAX_DEPTH = 10;
  let depth = 0;

  while (currentParentId && depth < MAX_DEPTH) {
    const parentTask = tasksMap.get(currentParentId);
    if (parentTask) {
      breadcrumbs.unshift({ id: parentTask.id, title: parentTask.title });
      currentParentId = parentTask.parentId;
    } else {
      currentParentId = null;
    }
    depth++;
  }
  return breadcrumbs;
};

/**
 * Calculates the index at which a new task input should be inserted.
 * @param {Task[]} tasks
 * @param {any} newTaskPosition
 * @returns {number}
 */
export const getNewTaskInsertIndex = (tasks, newTaskPosition) => {
  if (!newTaskPosition) return -1;
  if (newTaskPosition.nextKey === null) return tasks.length;

  const nextTaskIndex = tasks.findIndex(
    (t) => t.orderKey === newTaskPosition.nextKey,
  );
  if (nextTaskIndex !== -1) return nextTaskIndex;

  const prevTaskIndex = tasks.findIndex(
    (t) => t.orderKey === newTaskPosition.previousKey,
  );
  if (prevTaskIndex !== -1) return prevTaskIndex + 1;

  return -1;
};

/**
 * Traverses upward to find the ID of the root ancestor.
 * @param {string} startTaskId
 * @param {Map<string, Task>} tasksMap
 * @returns {string}
 */
export const getTrueRootId = (startTaskId, tasksMap) => {
  let currentId = startTaskId;
  let currentTask = tasksMap.get(currentId);
  const visited = new Set();

  while (
    currentTask &&
    currentTask.parentId &&
    tasksMap.has(currentTask.parentId) &&
    !visited.has(currentTask.id)
  ) {
    visited.add(currentId);
    currentId = currentTask.parentId;
    currentTask = tasksMap.get(currentId);
  }
  return currentId;
};

/**
 * Returns the most recent "touch" time (updatedAt or createdAt).
 * @param {Task | undefined} task
 * @returns {number}
 */
export const getTaskTouchTimeMs = (task) => {
  if (!task) return 0;
  const getMs = (v) => {
    if (!v) return 0;
    if (typeof v.toMillis === 'function') return v.toMillis();
    const ms = new Date(v).getTime();
    return isNaN(ms) ? 0 : ms;
  };
  return Math.max(getMs(task.updatedAt), getMs(task.createdAt));
};

/**
 * Retrieves the order keys surrounding a task identified by its ID.
 * Delegates to getKeysAtIndex when found; falls back to end-of-list when not.
 * @param {string} taskId
 * @param {Task[]} allTasks
 */
/** @public */
export const getKeysAroundTask = (taskId, allTasks) => {
  const index = allTasks.findIndex((t) => t.id === taskId);
  if (index < 0) {
    return getKeysAtEndOfList(allTasks);
  }
  return getKeysAtIndex(index, allTasks);
};

/**
 * Computes CSS indentation levels.
 * @param {Task} task
 * @param {string} indentationType
 */
export const computeIndentLevels = (task, indentationType) => {
  const baseLevel = task.materializedPath
    ? Math.max(0, task.materializedPath.split('.').length - 2)
    : 0;

  if (indentationType === 'full') {
    return { displayLevel: baseLevel, subtaskInputLevel: baseLevel + 1 };
  }
  if (indentationType === 'detail') {
    return { displayLevel: Math.max(0, baseLevel - 1), subtaskInputLevel: 1 };
  }
  return { displayLevel: 0, subtaskInputLevel: 1 };
};
